class Wishlist {
  constructor() {
    this.storageKey = 'wishlist_items';
    this.isLoggedIn = this.checkLoginStatus();
    this.customerId = window.customerId || null;
    this.init();
  }

  checkLoginStatus() {
    return window.customerId !== null && window.customerId !== undefined;
  }

  init() {
    if (this.isLoggedIn) {
      this.loadFromShopify();
      this.syncGuestWishlist(); // Sync any guest wishlist items
    } else {
      this.loadFromLocalStorage();
      this.showGuestNotification();
    }
    this.renderWishlist();
  }

  // Get wishlist items
  getItems() {
    if (this.isLoggedIn) {
      return this.shopifyWishlist || [];
    } else {
      const items = localStorage.getItem(this.storageKey);
      return items ? JSON.parse(items) : [];
    }
  }

  // Add item to wishlist
  addItem(productId, productTitle, productImage, productPrice, productUrl) {
    const items = this.getItems();
    
    if (items.find(item => item.id === productId)) {
      return false;
    }

    const newItem = {
      id: productId,
      title: productTitle,
      image: productImage,
      price: productPrice,
      url: productUrl,
      addedAt: new Date().toISOString()
    };

    items.push(newItem);

    if (this.isLoggedIn) {
      this.saveToShopify(items);
    } else {
      this.saveToLocalStorage(items);
    }

    this.updateCount();
    return true;
  }

  // Remove item from wishlist
  removeItem(productId) {
    let items = this.getItems();
    items = items.filter(item => item.id !== productId);

    if (this.isLoggedIn) {
      this.saveToShopify(items);
    } else {
      this.saveToLocalStorage(items);
    }

    this.updateCount();
    this.renderWishlist();
  }

  // Save to localStorage (for guests)
  saveToLocalStorage(items) {
    localStorage.setItem(this.storageKey, JSON.stringify(items));
  }

  // Load from localStorage
  loadFromLocalStorage() {
    const items = localStorage.getItem(this.storageKey);
    return items ? JSON.parse(items) : [];
  }

  // Load from Shopify customer metafields
  loadFromShopify() {
    if (window.customerWishlistData) {
      try {
        this.shopifyWishlist = typeof window.customerWishlistData === 'string' 
          ? JSON.parse(window.customerWishlistData) 
          : window.customerWishlistData;
      } catch (error) {
        console.error('Error parsing wishlist data:', error);
        this.shopifyWishlist = [];
      }
    } else {
      this.shopifyWishlist = [];
    }
  }

  // Save to Shopify customer metafields
  async saveToShopify(items) {
    if (!this.customerId) {
      console.error('No customer ID available');
      return;
    }

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            '_wishlist_update': JSON.stringify({
              customer_id: this.customerId,
              wishlist_data: items
            })
          }
        })
      });

      // Store in memory for immediate access
      this.shopifyWishlist = items;
      
      // Also update via metafield API
      await this.updateCustomerMetafield(items);
      
    } catch (error) {
      console.error('Error saving wishlist:', error);
      // Fallback to localStorage
      this.saveToLocalStorage(items);
    }
  }

  // Update customer metafield using Shopify's Customer API
  async updateCustomerMetafield(items) {
    try {
      const formData = new FormData();
      formData.append('customer[metafields][custom][wishlist]', JSON.stringify(items));
      
      const response = await fetch(`/account`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        console.warn('Metafield update via account failed, using alternative method');
        // Alternative: Store in session storage as backup
        sessionStorage.setItem('wishlist_backup', JSON.stringify(items));
      }
    } catch (error) {
      console.error('Error updating metafield:', error);
    }
  }

  // Sync guest wishlist to logged-in account
  async syncGuestWishlist() {
    const guestItems = this.loadFromLocalStorage();
    
    if (guestItems.length > 0) {
      // Merge guest items with existing Shopify wishlist
      const existingItems = this.shopifyWishlist || [];
      const mergedItems = [...existingItems];
      
      guestItems.forEach(guestItem => {
        if (!mergedItems.find(item => item.id === guestItem.id)) {
          mergedItems.push(guestItem);
        }
      });
      
      // Save merged wishlist
      await this.saveToShopify(mergedItems);
      
      // Clear localStorage
      localStorage.removeItem(this.storageKey);
      
      // Show notification
      if (guestItems.length > 0) {
        this.showToast(`${guestItems.length} item(s) synced to your account`);
      }
    }
  }

  // Show notification for guest users
  showGuestNotification() {
    const notification = document.getElementById('wishlist-notification');
    if (notification && !this.isLoggedIn) {
      notification.style.display = 'block';
    }
  }

  // Render wishlist items on the page
  renderWishlist() {
    const items = this.getItems();
    const container = document.getElementById('wishlist-items');
    const emptyState = document.getElementById('wishlist-empty');

    if (!container) return;

    if (items.length === 0) {
      container.style.display = 'none';
      if (emptyState) emptyState.style.display = 'block';
      return;
    }

    container.style.display = 'grid';
    if (emptyState) emptyState.style.display = 'none';

    container.innerHTML = items.map(item => `
      <div class="wishlist-item" data-product-id="${item.id}">
        <a href="${item.url}">
          <img src="${item.image}" alt="${item.title}" loading="lazy">
        </a>
        <div class="wishlist-item-details">
          <a href="${item.url}">
            <h3>${item.title}</h3>
          </a>
          <p class="price">${item.price}</p>
          <div class="wishlist-item-actions">
            <a href="${item.url}" class="button button--primary">View Product</a>
            <button class="wishlist-remove button button--secondary" data-product-id="${item.id}">Remove</button>
          </div>
        </div>
      </div>
    `).join('');

    // Add event listeners to remove buttons
    container.querySelectorAll('.wishlist-remove').forEach(button => {
      button.addEventListener('click', (e) => {
        const productId = e.target.dataset.productId;
        this.removeItem(productId);
      });
    });
  }

  // Update wishlist count in header
  updateCount() {
    const items = this.getItems();
    const countElement = document.getElementById('wishlist-count');
    const accessibilityElement = document.getElementById('wishlist-count-accessibility');
    if (countElement && accessibilityElement) {
      if (items.length > 0) {
        countElement.textContent = items.length > 99 ? '99+' : items.length;
        accessibilityElement.textContent = items.length > 99 ? '99+' : items.length;
      } else {
        countElement.textContent = '0';
        accessibilityElement.textContent = '0';
      }
    }
  }

  // Check if product is in wishlist
  hasItem(productId) {
    const items = this.getItems();
    return items.some(item => item.id === productId);
  }

  // Toggle wishlist item
  toggleItem(productId, productTitle, productImage, productPrice, productUrl) {
    if (this.hasItem(productId)) {
      this.removeItem(productId);
      return false;
    } else {
      this.addItem(productId, productTitle, productImage, productPrice, productUrl);
      return true;
    }
  }

  // Initialize wishlist buttons on product pages
  initButtons() {
    const buttons = document.querySelectorAll('.wishlist-toggle');
    
    buttons.forEach(button => {
      const productId = button.dataset.productId;
      
      // Set initial state
      if (this.hasItem(productId)) {
        button.classList.add('active');
        const textElement = button.querySelector('.wishlist-button-text');
        if (textElement) textElement.textContent = 'In Wishlist';
      }

      // Add click handler
      button.addEventListener('click', (e) => {
        e.preventDefault();
        
        const productTitle = button.dataset.productTitle;
        const productImage = button.dataset.productImage;
        const productPrice = button.dataset.productPrice;
        const productUrl = button.dataset.productUrl;
        
        const added = this.toggleItem(productId, productTitle, productImage, productPrice, productUrl);
        
        const textElement = button.querySelector('.wishlist-button-text');
        
        if (added) {
          button.classList.add('active');
          if (textElement) textElement.textContent = 'In Wishlist';
          this.showToast('Added to wishlist');
        } else {
          button.classList.remove('active');
          if (textElement) textElement.textContent = 'Add to Wishlist';
          this.showToast('Removed from wishlist');
        }
        
        this.updateCount();
      });
    });
  }

  // Show toast notification
  showToast(message) {
    const toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.classList.add('show');
    }, 100);
    
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  }

  observeHeaderChanges() {
    const countElement = document.getElementById('wishlist-count');
    if (!countElement) return;
  
    // Watch for changes to the header
    const header = document.querySelector('header') || document.querySelector('.header');
    if (!header) return;
  
    let isUpdating = false;
    
    const observer = new MutationObserver(() => {
      if (isUpdating) return; // Prevent infinite loop
      
      isUpdating = true;
      this.updateCount();
      setTimeout(() => {
        isUpdating = false; // Reset flag after update
      }, 100);
    });
  
    observer.observe(header, {
      childList: true,
      subtree: true
    });
  }
  
}


// Update your initialization
document.addEventListener('DOMContentLoaded', () => {
  window.wishlist = new Wishlist();
  window.wishlist.updateCount();
  window.wishlist.initButtons();
  window.wishlist.observeHeaderChanges(); // Add this line
});
